import React, { FC, JSX, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ThreeCircles } from 'react-loader-spinner';
import { useNavigate } from 'react-router';
import cn from 'classnames';

import { SearchList } from 'components/organisms';
import { SwipeTransition } from 'components/molecules';
import { Button, ToolTip } from 'components/atoms';
import { BackIcon, DownloadIcon, FileIcon, ManualIcon, RepeatIcon } from 'components/icons';
import { GET_HISTORY } from 'queries';
import downloadFile from 'utils/downloadFile';

import PATHS from 'routes/PATHS';

import './History.scss';

const History: FC = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['history'],
    queryFn: () => GET_HISTORY(),
  });

  const [showResult, setShowResult] = useState<{ show: boolean; index: number }>({
    show: false,
    index: -1,
  });
  const [resultRows, setResultRows] = useState<(string | number)[][]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (showResult.show) {
      generateResultEntries();
    }
  }, [showResult]);

  const generateHistoryColumns = () => [
    '#',
    'Email',
    'Operation Date',
    'Operation Type',
    'Prompt File',
    'Prompt Text',
    'Prompt Context',
    'Prompt Keywords',
    'Prompt Languages',
    'Number of Outputs',
    'Prompt Tones',
    'Prompt Topics',
    'Words Limit',
    'Actions',
  ];

  const handleRepeatQuery = () => {
    if (data?.data && 'data' in data.data) {
      navigate(`/${PATHS.MESSAGE}`, { state: data.data.data[showResult.index].prompt });
    }
  };

  const handleDownloadResultFile = () => {
    if (data?.data && 'data' in data.data) {
      downloadFile(
        data.data.data[showResult.index].base64_dataframe.toString(),
        data.data.data[showResult.index].output_file_name,
      );
    }
  };

  const generateHistoryEntries = (): (string | number | JSX.Element)[][] => {
    if (data?.data && 'data' in data.data) {
      data.data.data.sort((a, b) => {
        const dateA = new Date(a.query_time).getTime();
        const dateB = new Date(b.query_time).getTime();
        return dateB - dateA;
      });

      return data.data.data.map((item, index) => {
        const date = new Date(item.query_time).toString().split('(')[0];
        const typeFile = 'base64_input_file' in item.prompt;
        return [
          index + 1,
          item.email || '-',
          <>{date || '-'}</>,
          item.query_type === 'file' ? (
            <FileIcon key={'file-icon'} />
          ) : (
            <ManualIcon key={'manual-icon'} className={'message-form__manual-icon'} />
          ),
          typeFile ? (
            <ToolTip title={'Download prompt file'} offset={[0, -75]}>
              <Button
                onClick={(event) => {
                  event.stopPropagation();
                  downloadFile(
                    'base64_input_file' in item.prompt ? item.prompt.base64_input_file : '',
                    'input_file_name' in item.prompt ? item.prompt.input_file_name : '',
                  );
                }}
                className={'history-list__buttons'}
              >
                <DownloadIcon />
              </Button>
            </ToolTip>
          ) : (
            '-'
          ),
          !typeFile && 'text' in item.prompt ? item.prompt.text || '-' : '-',
          !typeFile && 'context' in item.prompt ? item.prompt.context || '-' : '-',
          !typeFile && 'keywords' in item.prompt ? (
            <div className={'history-list__iterable-box'}>
              {item.prompt.keywords.length
                ? item.prompt.keywords.map((item, index) => (
                    <p className={'history-list__iterable-item'} key={item + index}>
                      {item}
                    </p>
                  ))
                : '-'}
            </div>
          ) : (
            <>-</>
          ),
          !typeFile && 'output_languages' in item.prompt ? (
            <div className={'history-list__iterable-box'}>
              {item.prompt.output_languages.length
                ? item.prompt.output_languages.map((item, index) => (
                    <p className={'history-list__iterable-item'} key={item + index}>
                      {item}
                    </p>
                  ))
                : '-'}
            </div>
          ) : (
            <>-</>
          ),
          !typeFile && 'outputs_per_tone' in item.prompt
            ? item.prompt.outputs_per_tone || '-'
            : '-',
          !typeFile && 'tones' in item.prompt ? (
            <div className={'history-list__iterable-box'}>
              {item.prompt.tones.length
                ? item.prompt.tones.map((item, index) => (
                    <p className={'history-list__iterable-item'} key={item + index}>
                      {item}
                    </p>
                  ))
                : '-'}
            </div>
          ) : (
            <>-</>
          ),
          !typeFile && 'topics' in item.prompt ? (
            <div className={'history-list__iterable-box'}>
              {item.prompt.topics.length
                ? item.prompt.topics.map((item, index) => (
                    <p className={'history-list__iterable-item'} key={item + index}>
                      {item}
                    </p>
                  ))
                : '-'}
            </div>
          ) : (
            <>-</>
          ),
          !typeFile && 'word_limit' in item.prompt ? item.prompt.word_limit || '-' : '-',
          <div className={'history-list__actions'} key={index}>
            <ToolTip title={'Repeat query'} offset={[0, -75]}>
              <Button
                className={'history-list__buttons'}
                primary
                onClick={() => navigate(`/${PATHS.MESSAGE}`, { state: item.prompt })}
              >
                <RepeatIcon />
              </Button>
            </ToolTip>
            <ToolTip title={'Download result file'} offset={[0, -75]}>
              <Button
                onClick={(event) => {
                  event.stopPropagation();
                  downloadFile(item.base64_dataframe.toString(), item.output_file_name);
                }}
                className={'history-list__buttons'}
              >
                <DownloadIcon />
              </Button>
            </ToolTip>
          </div>,
        ];
      });
    }
    return [];
  };

  const generateResultColumns = (): string[] => {
    if (!data?.data || !('data' in data.data) || !showResult.show) {
      return [];
    }

    const allValues = Object.values(data.data.data[showResult.index].dataframe);
    if (!allValues.length) {
      return [];
    }

    const firstItem = allValues[0];
    if (!firstItem || typeof firstItem !== 'object') {
      return [];
    }

    const columns = Object.keys(firstItem);
    columns.unshift('#');
    return columns;
  };

  const generateResultEntries = () => {
    if (!data?.data || !('data' in data.data) || !showResult.show) {
      return [];
    }

    const rows: (string | number)[][] = [];

    Object.entries(data.data.data[showResult.index].dataframe).forEach(([key, item]) => {
      const rowValues = Object.values(item);
      rowValues.unshift(Number(key));
      rows.push(rowValues);
    });

    setResultRows(rows);
  };

  return (
    <section className={'section section-center history no-scroll'}>
      <div className={'history__control'} style={{ opacity: showResult.show ? 1 : 0 }}>
        <Button
          className={'history__control-item'}
          primary
          onClick={() => setShowResult({ show: false, index: -1 })}
        >
          <BackIcon />
        </Button>
        <Button className={'history__control-item'} primary onClick={handleRepeatQuery}>
          <RepeatIcon />
        </Button>
        <Button onClick={handleDownloadResultFile} className={'history__control-item'}>
          <DownloadIcon />
        </Button>
      </div>
      {isLoading ? <></> : <p className="history__head-text">Message History</p>}
      <div className={'history-list-box'}>
        {isLoading ? (
          <ThreeCircles
            visible={true}
            height="100"
            width="100"
            color="white"
            wrapperStyle={{}}
            wrapperClass="history-list__loader"
          />
        ) : (
          <SwipeTransition
            elements={[
              <SearchList
                className={'history-list'}
                key={'history-results'}
                rows={resultRows}
                columns={generateResultColumns()}
                searchBy={'All'}
                sortBy={'All'}
                rowClickFunc={() => {}}
              />,
              <SearchList
                className={'history-list'}
                key={'history-list'}
                rows={generateHistoryEntries()}
                columns={generateHistoryColumns()}
                searchBy={'All'}
                sortBy={'All'}
                rowClickFunc={(item, index) => setShowResult({ show: true, index })}
              />,
            ]}
            hide={true}
            state={showResult.show}
            className={cn('history-swipe')}
          />
        )}
      </div>
    </section>
  );
};

export default History;
